import {
	ErrorModal,
	ShiftDayNavigatorControl,
	ShiftInfo,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Asset } from "@dexteel/mesf-core/dist/models/Asset";
import {
	Checkbox,
	Divider,
	FormControlLabel,
	FormGroup,
	Grid,
	Paper,
	Typography,
} from "@material-ui/core";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	LazyBackDropLoading,
	LazyLoading,
} from "../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { getDelayAreas } from "../delays-report/repositories/DelaysReportRepository";
import { DelayAreaFilter } from "./components/DelayAreaFilter";
import { DelaysManagerTable } from "./components/DelaysManagerTable";
import { useDelaysManagerContext } from "./context/DelaysManagerContext";
import { getDelaysByShift } from "./repositories/DelaysManagerRepository";

type Props = {
	asset: "caster" | "eaf";
};

export const DelaysManagerPage = ({ asset }: Props) => {
	const assetId = asset === "caster" ? 13 : asset === "eaf" ? 14 : null;
	const assetName =
		asset === "caster" ? "Caster" : asset === "eaf" ? "EAF" : null;

	const { state, actions } = useDelaysManagerContext();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const [selectedDelayArea, setSelectedDelayArea] = useState<number | null>(
		assetId,
	);
	const [showMicroDelays, setShowMicroDelays] = useState(false);
	const [isLoadingFilter, setIsLoadingFilter] = useState(false);

	const { shiftId } = useParams();
	const [searchParams] = useSearchParams();
	const [firstRender, setFirstRender] = useState(true);

	const navigate = useNavigate();

	const getDataFromAPI = async (shiftParam?: ShiftInfo) => {
		try {
			actions.setIsLoadingGrid(true);
			setIsLoadingFilter(true);

			if (!state.assetForDelayArea || state.assetForDelayArea.length === 0) {
				const response = await getDelayAreas();
				if (response.ok) {
					actions.setAssetForDelayArea(response.data.tables[0].rows);
				} else {
					actions.setErrorMessage("Error loading delay areas");
					return;
				}
			}
			const shift = get(
				shiftParam ? shiftParam : shiftInfo,
				"CurrentShiftId",
				0,
			);
			const resp = await getDelaysByShift(
				shift || 0,
				selectedDelayArea,
				showMicroDelays,
			);
			if (resp.ok && resp.data.tables.length > 0) {
				actions.setDelays(resp.data.tables[0].rows);
			} else {
				actions.setErrorMessage("Error loading delays");
			}
		} catch (error) {
			actions.setErrorMessage("An unexpected error occurred");
		} finally {
			actions.setIsLoadingGrid(false);
			setIsLoadingFilter(false);
		}
	};
	useEffect(() => {
		setSelectedDelayArea(assetId);
	}, [asset, assetId]);

	useEffect(() => {
		if (shiftInfo && !firstRender) {
			const shiftIdValue = get(shiftInfo, "CurrentShiftId", null);
			const delayId = searchParams.get("delayId");
			const queryParams = [];

			if (delayId) {
				queryParams.push(`delayId=${delayId}`);
			}
			const query = queryParams.length ? `?${queryParams.join("&")}` : "";
			const basePath = `/${asset}-delays-manager`;
			navigate(`${basePath}/${shiftIdValue}${query}`);
			(async () => {
				await getDataFromAPI();
			})();
		}
	}, [shiftInfo, selectedDelayArea, showMicroDelays]);

	useEffect(() => {
		if (shiftId) {
			(async () => {
				getShiftDataFromAPI(null, Number(shiftId)).then(async (shift) => {
					if (shift) {
						getDataFromAPI(shift).then(() => {
							setFirstRender(false);
						});
					}
				});
			})();
		} else {
			(async () => {
				getShiftDataFromAPI(null, 0).then(async (shift) => {
					if (shift) {
						getDataFromAPI(shift).then(() => {
							setFirstRender(false);
						});
					}
				});
			})();
		}
	}, []);

	const getFilteredDelayAreas = () => {
		if (asset === "eaf") {
			const areas = state.assetForDelayArea || [];
			const filtered = areas.filter(
				(area: Asset) => area.AssetName && area.AssetName.includes("EAF "),
			);
			if (assetId) return [{ AssetId: assetId, AssetName: "All" }, ...filtered];
		}
		return state.assetForDelayArea || [];
	};

	return !state.isLoadingPage && assetId ? (
		<Paper
			elevation={0}
			style={{
				width: "100%",
				padding: "15px 20px 10px",
				height: "93vh",
				backgroundColor: "#f2f4f7",
			}}
		>
			<LazyBackDropLoading open={state.isLoadingBackground} />
			<Grid container justifyContent="center" alignItems="center">
				<Grid item md={12} xs={12} style={{ margin: 0 }}>
					<Grid item md={12} xs={12}>
						<Typography variant="h5" style={{ fontWeight: 600 }}>
							{assetName} Delays Manager
						</Typography>
					</Grid>
					<Grid item md={12} xs={12}>
						<Grid
							container
							direction="row"
							justifyContent="flex-start"
							alignItems="center"
						>
							<ShiftDayNavigatorControl preventInit />
							<Paper
								style={{
									minWidth: "300px",
									margin: "10px 10px",
									height: "90px",
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<Grid
									item
									container
									xs={12}
									spacing={2}
									alignItems="center"
									style={{ marginLeft: "10px" }}
									direction="row"
								>
									{asset === "eaf" && (
										<Grid item>
											<DelayAreaFilter
												delayAreas={getFilteredDelayAreas()}
												selectedDelayArea={selectedDelayArea}
												isLoading={isLoadingFilter}
												onChange={(newAssetId) => {
													setSelectedDelayArea(newAssetId);
												}}
											/>
										</Grid>
									)}
									<Grid item>
										<FormGroup>
											<FormControlLabel
												control={
													<Checkbox
														checked={showMicroDelays}
														color="primary"
														onChange={(event) =>
															setShowMicroDelays(event.target.checked)
														}
													/>
												}
												label="Microdelays"
											/>
										</FormGroup>
									</Grid>
								</Grid>
							</Paper>
						</Grid>
					</Grid>
					<ErrorModal
						error={state.errorMessage}
						onHide={() => actions.setErrorMessage("")}
					/>
					<NotificationSnackBar
						message={state.notificationMessage}
						onHide={() => actions.setNotificationMessage("")}
					/>
					<DelaysManagerTable
						loadingGrid={state.isLoadingGrid}
						rows={state.delays || []}
						refreshData={getDataFromAPI}
					/>
				</Grid>
			</Grid>
		</Paper>
	) : (
		<LazyLoading />
	);
};
